/**
 * The identity bar and area tabs shared by every Deal Model area (Editor,
 * Data, Fixtures & tests). The Editor adds its own actions (save state,
 * verdict, compile status, Save) to the end of the identity row.
 */
import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { IconChevronLeft, IconDatabase, IconFlask, IconPencil } from '@tabler/icons-react'
import type { DealModelArea, DealModelSummary } from '../../api/dealModels'
import { Pill } from '../../ui/kit'

export function shortIdentity(value: string): string {
  return value.length > 18 ? `${value.slice(0, 9)}…${value.slice(-7)}` : value
}

export function stateTone(state: DealModelSummary['state']): 'good' | 'bad' | undefined {
  return state === 'ready' ? 'good' : state === 'invalid' ? 'bad' : undefined
}

export function ModelStatePill({ state }: { state: DealModelSummary['state'] }) {
  const tone = stateTone(state)
  return <Pill tone={tone} muted={tone === undefined}>{state}</Pill>
}

export function WorkspaceHead({ model, area, actions }: { model: DealModelSummary; area: DealModelArea; actions?: ReactNode }) {
  const modelId = model.logicalModelId
  return (
    <>
      <div className="ident dm-ident">
        <Link to="/deal-models" className="btn is-small" aria-label="Back to Deal Models"><IconChevronLeft className="ic-sm" />Deal Models</Link>
        <h1 className="addr">{model.name}</h1>
        <ModelStatePill state={model.state} />
        {!model.capabilities.edit ? <Pill muted>Inspect only</Pill> : null}
        <span className="meta">Revision {model.revision} · source <span className="t-mono" title={model.sourceSha256}>{shortIdentity(model.sourceSha256)}</span></span>
        {actions ? <div className="dm-ident-actions">{actions}</div> : null}
      </div>
      <nav className="tabs" aria-label="Deal Model areas">
        <Link to="/deal-models/$modelId/editor" params={{ modelId }} search={{}} className={area === 'editor' ? 'is-on' : ''} aria-current={area === 'editor' ? 'page' : undefined}><IconPencil className="ic-sm" />Editor</Link>
        <Link to="/deal-models/$modelId/data" params={{ modelId }} search={{}} className={area === 'data' ? 'is-on' : ''} aria-current={area === 'data' ? 'page' : undefined}><IconDatabase className="ic-sm" />Data</Link>
        <Link to="/deal-models/$modelId/fixtures" params={{ modelId }} search={{}} className={area === 'fixtures' ? 'is-on' : ''} aria-current={area === 'fixtures' ? 'page' : undefined}><IconFlask className="ic-sm" />Fixtures &amp; tests</Link>
      </nav>
    </>
  )
}
